import { Dialog, DialogProps as MuiDialogProps, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ComponentType, FC } from 'react';

export interface DialogProps {
  // If the dialog is gated, open defaults to true unless explicitly set.
  open?: boolean;
  onClose?: MuiDialogProps['onClose'];
  dialogProps?: Omit<MuiDialogProps, 'children' | 'open'>;
}

type Nullable<T> = { [P in keyof T]: T[P] | null };

interface AsDialog<InputProps extends {}, GatedContextField extends keyof InputProps> {
  Component: ComponentType<InputProps>;

  // If the dialog is gated by a context field, its value must be truthy and as
  // such is it also inferred for the consumer. This reduces the redundancy of
  // passing both open and a context value as properties.
  //
  // E.g.:
  //
  //   <Dialog context={context} />
  //
  // instead of
  //
  // <Dialog open={!!context} context={context} />
  gatedContextField?: GatedContextField;
}

const asDialog = <InputProps extends {}, GatedContextField extends keyof InputProps>({
  Component,
  gatedContextField,
}: AsDialog<InputProps, GatedContextField>): FC<
  Omit<InputProps, GatedContextField> & Nullable<Pick<InputProps, GatedContextField>> & DialogProps
> => {
  const isGated = !!gatedContextField;

  return ({ open = isGated, onClose, dialogProps, ...props }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const isContextConsideredAvailable = gatedContextField ? !!props[gatedContextField as keyof typeof props] : true;

    return (
      <Dialog
        fullScreen={fullScreen}
        open={isContextConsideredAvailable && open}
        {...dialogProps}
        onClose={dialogProps?.onClose ?? onClose}
      >
        <Component {...(props as unknown as InputProps)} onClose={onClose} />
      </Dialog>
    );
  };
};

export default asDialog;
